import {
    SEARCH_BY_DATE_AFTER,
    SEARCH_BY_DATE_BEFORE,
    SEARCH_BY_LIST_EXCLUDE,
    SEARCH_BY_NUMBER_LESS,
    SEARCH_BY_NUMBER_MORE,
    SEARCH_BY_PERIOD,
    SEARCH_BY_RANGE,
    SEARCH_BY_STRING,
} from "@/constants/tableFilters";

const SORTS = {
    holder: "worker",
    expiryDate: "exp_date",
    manufactureDate: "manuf_date",
    purchaseDate: "purch_date",
    purchasePlace: "purch_place",
};

const FILTERS = {
    category: ({ searchType, values }) => {
        const category = values;
        const category_exclude = searchType === SEARCH_BY_LIST_EXCLUDE ? 1 : 0;

        return {
            category,
            category_exclude,
        };
    },
    subcategory: ({ searchType, value }) => {
        if (searchType === SEARCH_BY_STRING) {
            return {
                subcategory: value,
            };
        }
        return {
            subcategory_has: value,
        };
    },
    type: ({ searchType, value }) => {
        if (searchType === SEARCH_BY_STRING) {
            return { type: value };
        }
        return {
            type_has: value,
        };
    },
    guid: ({ searchType, value }) => {
        if (searchType === SEARCH_BY_STRING) {
            return { guid: value };
        }
        return {
            guid_has: value,
        };
    },
    name: ({ searchType, value }) => {
        if (searchType === SEARCH_BY_STRING) {
            return { inventory: value };
        }
        return {
            inventory_has: value,
        };
    },
    holder: ({ searchType, value }) => {
        if (searchType === SEARCH_BY_STRING) {
            return { worker_name: value };
        }
        return {
            worker_name_has: value,
        };
    },
    expiryDate: ({ searchType, value, from, to }) => {
        if (searchType === SEARCH_BY_PERIOD) {
            return {
                exp_date_search_type: "period",
                exp_date_after: from,
                exp_date_before: to,
            };
        }

        if (searchType === SEARCH_BY_DATE_BEFORE) {
            return {
                exp_date_search_type: "before",
                exp_date_value: value,
                exp_date_before: value,
            };
        }

        if (searchType === SEARCH_BY_DATE_AFTER) {
            return {
                exp_date_search_type: "after",
                exp_date_value: value,
                exp_date_after: value,
            };
        }

        return {
            exp_date_search_type: "date",
            exp_date_value: value,
            exp_date_before: value,
            exp_date_after: value,
        };
    },
    cost: ({ searchType, value, from, to }) => {
        if (searchType === SEARCH_BY_RANGE) {
            return {
                cost_from: from,
                cost_to: to,
            };
        }

        if (searchType === SEARCH_BY_NUMBER_LESS) {
            return {
                cost_to: value,
            };
        }

        if (searchType === SEARCH_BY_NUMBER_MORE) {
            return {
                cost_from: value,
            };
        }

        return {
            cost_from: value,
            cost_to: value,
        };
    },
    manufactureDate: ({ searchType, value, from, to }) => {
        if (searchType === SEARCH_BY_PERIOD) {
            return {
                manuf_date_search_type: "period",
                manuf_date_before: to,
                manuf_date_after: from,
            };
        }

        if (searchType === SEARCH_BY_DATE_BEFORE) {
            return {
                manuf_date_search_type: "before",
                manuf_date_before: value,
                manuf_date_value: value,
            };
        }

        if (searchType === SEARCH_BY_DATE_AFTER) {
            return {
                manuf_date_search_type: "after",
                manuf_date_after: value,
                manuf_date_value: value,
            };
        }

        return {
            manuf_date_search_type: "date",
            manuf_date_before: value,
            manuf_date_after: value,
            manuf_date_value: value,
        };
    },
    purchaseDate: ({ searchType, value, from, to }) => {
        if (searchType === SEARCH_BY_PERIOD) {
            return {
                purch_date_before: to,
                purch_date_after: from,
                purch_date_search_type: "period",
            };
        }

        if (searchType === SEARCH_BY_DATE_BEFORE) {
            return {
                purch_date_before: value,
                purch_date_value: value,
                purch_date_search_type: "before",
            };
        }

        if (searchType === SEARCH_BY_DATE_AFTER) {
            return {
                purch_date_after: value,
                purch_date_value: value,
                purch_date_search_type: "after",
            };
        }

        return {
            purch_date_before: value,
            purch_date_after: value,
            purch_date_value: value,
            purch_date_search_type: "date",
        };
    },
    model: ({ searchType, value }) => {
        if (searchType === SEARCH_BY_STRING) {
            return { model: value };
        }
        return {
            model_has: value,
        };
    },
    purchasePlace: ({ searchType, value }) => {
        if (searchType === SEARCH_BY_STRING) {
            return { purch_place: value };
        }
        return {
            purch_place_has: value,
        };
    },
};

function formatFilters(filters) {
    let result = {};

    Object.entries(filters).forEach(([filterName, filter]) => {
        const { type, value } = filter;

        if (!value) return;

        const formatter = FILTERS[filterName];

        if (!formatter) return;

        result = {
            ...result,
            ...formatter(value, type),
        };
    });

    return result;
}

export function TableModel() {
    const instance = {
        page: 1,
        filters: {
            category: { type: "list", value: null },
            subcategory: { type: "string", value: null },
            type: { type: "string", value: null },
            guid: { type: "string", value: null },
            name: { type: "string", value: null },
            holder: { type: "string", value: null },
            expiryDate: { type: "date", value: null },
            cost: { type: "number", value: null },
            manufactureDate: { type: "date", value: null },
            purchaseDate: { type: "date", value: null },
            model: { type: "string", value: null },
            purchasePlace: { type: "string", value: null },
        },
        sortBy: "",
        desc: false,
    };

    function getOrder() {
        let sort = instance.sortBy;
        if (!sort) return "-id";
        if (SORTS[sort]) sort = SORTS[sort];
        return `${instance.desc ? "-" : ""}${sort}`;
    }

    instance.init = async function init(query) {
        instance.sortBy = query.sortBy || "";
        instance.desc = query.desc === "1";
        Object.keys(instance.filters).forEach((key) => {
            if (query[key]) {
                try {
                    instance.filters[key].value = JSON.parse(query[key]);
                } catch (e) {
                    console.warn(e);
                }
            }
        });
    };

    instance.getParams = function getParams() {
        const params = {
            page: instance.page,
            ...formatFilters(instance.filters),
        };
        const sort = getOrder();
        if (sort) {
            params.sort = sort;
        }
        return params;
    };

    instance.getUrlParams = function getUrlParams() {
        const params = {
            sortBy: instance.sortBy,
            desc: instance.desc ? 1 : 0,
        };

        Object.entries(instance.filters).forEach(([key, filter]) => {
            if (!filter.value) return;

            params[key] = JSON.stringify(filter.value);
        });

        return params;
    };

    instance.sort = function sort(columnName) {
        if (instance.sortBy === columnName) {
            instance.desc = !instance.desc;
        } else {
            instance.sortBy = columnName;
            instance.desc = false;
        }
    };
    instance.nextPage = function nextPage() {
        instance.page += 1;
    };
    instance.reset = function reset() {
        instance.page = 1;
    };

    instance.resetFilters = function resetFilters() {
        Object.keys(instance.filters).forEach((key) => {
            instance.filters[key].value = null;
        });
        instance.sortBy = "";
        instance.desc = false;
    };

    return instance;
}
